import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import axios from "axios";
import { 
  Lock, 
  Eye, 
  EyeOff, 
  AlertCircle, 
  CheckCircle2, 
  Loader,
  ArrowLeft,
  Key,
  Check,
  X
} from "lucide-react";

const SetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const uid = searchParams.get("uid");
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [validToken, setValidToken] = useState(null);

  // Password requirements state
  const [requirements, setRequirements] = useState({
    minLength: false,
    hasUpper: false,
    hasLower: false,
    hasNumber: false,
    hasSpecial: false,
  });

  const [passwordStrength, setPasswordStrength] = useState("");
  const [passwordStrengthColor, setPasswordStrengthColor] = useState("");

  // Validate token on mount
  useEffect(() => {
    if (!uid || !token) {
      setValidToken(false);
      setMessage("Invalid or missing setup link.");
      setMessageType("error");
    } else {
      setValidToken(true);
    }
  }, [uid, token]);

  // Update password requirements as user types
  useEffect(() => {
    if (!password) {
      setRequirements({
        minLength: false,
        hasUpper: false,
        hasLower: false,
        hasNumber: false,
        hasSpecial: false,
      });
      setPasswordStrength("");
      return;
    }

    const newReqs = {
      minLength: password.length >= 8,
      hasUpper: /[A-Z]/.test(password),
      hasLower: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    setRequirements(newReqs);

    // Calculate strength
    const metRequirements = Object.values(newReqs).filter(Boolean).length;
    if (metRequirements <= 1) {
      setPasswordStrength("Weak");
      setPasswordStrengthColor("text-red-500");
    } else if (metRequirements <= 2) {
      setPasswordStrength("Fair");
      setPasswordStrengthColor("text-yellow-500");
    } else if (metRequirements <= 3) {
      setPasswordStrength("Good");
      setPasswordStrengthColor("text-blue-500");
    } else {
      setPasswordStrength("Strong");
      setPasswordStrengthColor("text-green-500");
    }
  }, [password]);

  // Password strength validator
  const validatePassword = () => {
    if (password.length < 8) return "Password must be at least 8 characters.";
    if (!/[A-Z]/.test(password)) return "Password must contain at least one uppercase letter.";
    if (!/[a-z]/.test(password)) return "Password must contain at least one lowercase letter.";
    if (!/\d/.test(password)) return "Password must contain at least one number.";
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return "Password must contain at least one special character.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    // Validation
    if (password !== confirmPassword) {
      setMessage("Passwords do not match.");
      setMessageType("error");
      return;
    }

    const errorMsg = validatePassword();
    if (errorMsg) {
      setMessage(errorMsg);
      setMessageType("error");
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}users/set-password/`,
        { uid, token, password },
        { withCredentials: true }
      );

      setMessage("Password successfully set! Redirecting to login...");
      setMessageType("success");
      setSuccess(true);

      // Clear any previous auth info
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user_profile");

      // Redirect to login
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      console.error("Set password error:", error);
      if (error.response?.status === 400) {
        setMessage("This setup link has expired or is invalid. Please request a new one.");
      } else {
        setMessage(
          error.response?.data?.error ||
          "Failed to set password. Please try again."
        );
      }
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  // Invalid Token State
  if (validToken === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4 py-12 relative overflow-hidden">
        <Helmet>
          <title>Invalid Setup Link | Governance Tool</title>
        </Helmet>

        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF4D00] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
        </div>

        <div className="w-full max-w-md relative z-10">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
            <div className="bg-gradient-to-r from-red-600 to-red-700 px-8 py-6">
              <p className="text-red-100 text-sm font-medium">Invalid Setup Link</p>
            </div>

            <div className="p-8 sm:p-10 text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Link Expired or Invalid</h2>
              <p className="text-gray-600 mb-8">
                This setup link is either invalid or has expired. Please contact your administrator to request a new setup link.
              </p>

              <button
                onClick={() => navigate("/login")}
                className="w-full bg-gradient-to-r from-[#FF4D00] to-orange-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition"
              >
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success State
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4 py-12 relative overflow-hidden">
        <Helmet>
          <title>Password Set Successfully | Governance Tool</title>
        </Helmet>

        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-green-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
        </div>

        <div className="w-full max-w-md relative z-10">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-8 py-6">
              <p className="text-green-100 text-sm font-medium">Success</p>
            </div>

            <div className="p-8 sm:p-10 text-center">
              <div className="mb-6">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto animate-bounce" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Password Set Successfully!</h2>
              <p className="text-gray-600 mb-2">{message}</p>
              <p className="text-sm text-gray-500 mb-8">
                Redirecting you to login in 3 seconds...
              </p>

              <button
                onClick={() => navigate("/login")}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition flex items-center justify-center gap-2"
              >
                Go to Login Now
                <ArrowLeft size={20} className="rotate-180" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Form State
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4 py-12 relative overflow-hidden">
      <Helmet>
        <title>Set Password | Governance & Continuity Assessment Tool</title>
        <meta name="description" content="Set up your account password to get started." />
      </Helmet>

      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF4D00] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse" style={{ animationDelay: "2s" }}></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo/Brand */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#FF4D00] to-orange-600 rounded-2xl mb-4 shadow-lg">
            <Key className="text-white" size={32} />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Set Your Password</h1>
          <p className="text-gray-600">Create a strong password to get started</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#FF4D00] to-orange-600 px-8 py-6">
            <p className="text-orange-100 text-sm font-medium">
              Secure Account Setup
            </p>
          </div>

          {/* Form Content */}
          <div className="p-8 sm:p-10">
            {/* Error Message */}
            {message && messageType === "error" && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-semibold text-red-900">Error</p>
                  <p className="text-red-800 text-sm mt-1">{message}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* New Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-900 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a strong password"
                    disabled={loading}
                    className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4D00] focus:border-transparent transition disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#FF4D00] transition disabled:cursor-not-allowed"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff size={20} />
                    ) : (
                      <Eye size={20} />
                    )}
                  </button>
                </div>

                {/* Password Strength */}
                {password && (
                  <div className="mt-3">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all ${
                            passwordStrength === "Weak"
                              ? "w-1/4 bg-red-500"
                              : passwordStrength === "Fair"
                              ? "w-1/2 bg-yellow-500"
                              : passwordStrength === "Good"
                              ? "w-3/4 bg-blue-500"
                              : "w-full bg-green-500"
                          }`}
                        ></div>
                      </div>
                      <span className={`text-xs font-semibold ${passwordStrengthColor}`}>
                        {passwordStrength}
                      </span>
                    </div>

                    {/* Requirements Checklist */}
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center gap-2">
                        {requirements.minLength ? (
                          <Check className="text-green-500 flex-shrink-0" size={16} />
                        ) : (
                          <X className="text-gray-300 flex-shrink-0" size={16} />
                        )}
                        <span className={requirements.minLength ? "text-gray-700 font-medium" : "text-gray-500"}>
                          At least 8 characters
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {requirements.hasUpper ? (
                          <Check className="text-green-500 flex-shrink-0" size={16} />
                        ) : (
                          <X className="text-gray-300 flex-shrink-0" size={16} />
                        )}
                        <span className={requirements.hasUpper ? "text-gray-700 font-medium" : "text-gray-500"}>
                          One uppercase letter (A-Z)
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {requirements.hasLower ? (
                          <Check className="text-green-500 flex-shrink-0" size={16} />
                        ) : (
                          <X className="text-gray-300 flex-shrink-0" size={16} />
                        )}
                        <span className={requirements.hasLower ? "text-gray-700 font-medium" : "text-gray-500"}>
                          One lowercase letter (a-z)
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {requirements.hasNumber ? (
                          <Check className="text-green-500 flex-shrink-0" size={16} />
                        ) : (
                          <X className="text-gray-300 flex-shrink-0" size={16} />
                        )}
                        <span className={requirements.hasNumber ? "text-gray-700 font-medium" : "text-gray-500"}>
                          One number (0-9)
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {requirements.hasSpecial ? (
                          <Check className="text-green-500 flex-shrink-0" size={16} />
                        ) : (
                          <X className="text-gray-300 flex-shrink-0" size={16} />
                        )}
                        <span className={requirements.hasSpecial ? "text-gray-700 font-medium" : "text-gray-500"}>
                          One special character (!@#$%^&*)
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-900 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    disabled={loading}
                    className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4D00] focus:border-transparent transition disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={loading}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#FF4D00] transition disabled:cursor-not-allowed"
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={20} />
                    ) : (
                      <Eye size={20} />
                    )}
                  </button>
                </div>

                {/* Password Match Indicator */}
                {confirmPassword && password && (
                  <div className="mt-2 flex items-center gap-2">
                    {password === confirmPassword ? (
                      <>
                        <CheckCircle2 className="text-green-500 flex-shrink-0" size={16} />
                        <span className="text-xs text-green-600 font-medium">Passwords match</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="text-red-500 flex-shrink-0" size={16} />
                        <span className="text-xs text-red-600 font-medium">Passwords don't match</span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !password || !confirmPassword || password !== confirmPassword || Object.values(requirements).some(v => !v)}
                className="w-full bg-gradient-to-r from-[#FF4D00] to-orange-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    Setting Password...
                  </>
                ) : (
                  <>
                    Set Password
                  </>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Already have an account?</span>
              </div>
            </div>

            {/* Login Link */}
            <button
              onClick={() => navigate("/login")}
              type="button"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm font-medium text-gray-700"
            >
              Go to Login
            </button>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-8 py-6 border-t border-gray-200 text-center text-xs text-gray-500">
            <p>
              This is your first-time setup. Make sure to create a strong password.
            </p>
          </div>
        </div>

        {/* Security Tips */}
        <div className="mt-8 p-6 bg-white rounded-xl border border-gray-200 shadow">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <AlertCircle size={20} className="text-[#FF4D00]" />
            Security Tips
          </h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-[#FF4D00] font-bold">•</span>
              <span>Create a unique password not used on other sites</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#FF4D00] font-bold">•</span>
              <span>Use a combination of letters, numbers & symbols</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#FF4D00] font-bold">•</span>
              <span>Never share your password with anyone</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;